import { useQuery } from '@tanstack/react-query';
import { fetchAuditDigitization } from '@/api/audits';
import type {
  AuditDigitizationRecord,
  AuditDigitized,
} from '@/api/audits/types';
import { ApiError } from '@/lib/utils/apiFetch';

export const auditDigitizationKeys = {
  detail: (id: string) => ['audits', 'digitization', id] as const,
};

/** Map the raw digitization record to the client shape. `description`,
 *  `overall_rating`, `findings` and `sections` live inside `extractedSections`;
 *  `summary` comes from the separate `auditSummary` column. */
function mapRecord(record: AuditDigitizationRecord): AuditDigitized {
  const extracted = record.extractedSections;
  return {
    digitizedAuditId: record.id,
    status: record.status ?? null,
    percentage: record.percentage ?? 0,
    description: extracted?.description ?? '',
    summary: record.auditSummary ?? '',
    overall_rating: extracted?.overall_rating ?? null,
    findings: extracted?.findings ?? [],
    sections: extracted?.sections ?? [],
    extractedDatapoints: record.extractedDatapoints ?? null,
  };
}

/** Fetch + map the digitization record, resolving a 404 (`digitization_not_found`)
 *  to `null`. Exported so the route loader can warm the same cache key. */
export async function loadAuditDigitization(
  auditId: string,
): Promise<AuditDigitized | null> {
  try {
    const res = await fetchAuditDigitization(auditId);
    return mapRecord(res.data);
  } catch (error) {
    // A 404 means the audit has not been digitized yet — a valid state, not
    // an error. Anything else propagates so React Query surfaces it.
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

export function useAuditDigitization(auditId: string) {
  return useQuery({
    queryKey: auditDigitizationKeys.detail(auditId),
    queryFn: () => loadAuditDigitization(auditId),
    enabled: !!auditId,
    staleTime: 60_000,
    // Poll while extraction is in progress; the same query then holds the
    // finished content once it completes.
    refetchInterval: query => {
      const data = query.state.data;
      if (!data) return false;
      if (
        data.status === 'COMPLETED' ||
        data.status === 'FAILED' ||
        data.percentage === 100
      ) {
        return false;
      }
      return data.status === 'PROCESSING' ? 5000 : false;
    },
    refetchIntervalInBackground: false,
  });
}
